import { useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useCurrentLocale } from '@locales/client';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { useLoyaltyList } from '@/flat-pages/status/model/hooks';
// import { CROWN_IMAGE_URL } from '@/flat-pages/status/ui/status-card';// do not do like it
import { getBrandData } from '@/shared/hooks/useBrandTheme';
import {
  BannerData,
  BANNERS_BY_ID,
  isBettingBanner,
  SORT_CONFIG_BANNERS,
  SORT_CONFIG_BANNERS_PH_MOBILE,
  AUTH_ONLY_MENU_BANNERS,
  PH_ONLY_BANNERS,
} from '@/entities/minibanner/model/bannerHelper';

export const CROWN_IMAGE_URL = '/images/icons/status_new/status_level_';

interface UseBannersResult {
  sortedBanners: BannerData[];
  sortedMobileBanners: BannerData[];
  linkBanners: (option: string) => string;
  linkMobileBanners: (option: Omit<BannerData, 'counter'>) => string;
  categoryParam: string;
}

interface UseBannersProps {
  mobileLandscape: boolean;
}

const useBanners = ({ mobileLandscape }: UseBannersProps): UseBannersResult => {
  const { loyaltyList } = useLoyaltyList();
  const { user } = useUser();
  const pathname = usePathname();
  const locale = useCurrentLocale();
  const isLm = getBrandData().brandIsLm;
  const isPw = getBrandData().brandIsPw;
  const isPh = getBrandData().brandIsPh;
  const bettingEnabled = getBrandData().brandTheme.features.betting;

  const categoryParam = useMemo(
    () => pathname.match(/\/games\/([^\/]+)/)?.[1] || '',
    [pathname]
  );

  const gamesIndex = pathname.indexOf('games');
  const basePath = pathname.substring(0, gamesIndex + 5);

  const linkBanners = (option: string): string => {
    if (gamesIndex === -1 && option === 'favorite') {
      option = `games/favorite`;
    }

    if (option === 'https://t.me/pingwincasino') {
      return isLm ? 'https://t.me/lotomillion_channel' : option;
    }

    if (gamesIndex !== -1 && option === 'betting') {
      return `/${locale}/betting`;
    }

    // switch (option) {
    //   case 'betting_sports':
    //     return `betting?page=sport`;
    //   case 'betting_live':
    //     return `betting?page=live`;
    //   case 'betting_result':
    //     return `betting?page=results`;
    // }

    return `${basePath}/${option}`;
  };

  const linkMobileBanners = (option: Omit<BannerData, 'counter'>): string => {
    if (option.disabled) return '#';
    if (option.param.startsWith('https')) return option.param;

    const param = option.param === 'favorite' ? 'games/favorite' : option.param;

    return `/${locale}/${param}`;
  };

  const processBanners = useCallback(
    (order: number[]): BannerData[] => {
      const result: BannerData[] = [];

      for (const id of order) {
        if (!bettingEnabled && isBettingBanner(id)) continue;
        if (!user.isAuth && AUTH_ONLY_MENU_BANNERS.has(id)) continue;
        if (!isPh && PH_ONLY_BANNERS.has(id)) continue;

        const banner = BANNERS_BY_ID.get(id);
        if (!banner) continue;

        if (banner.id === 30 && !isPw) {
          continue;
        }
        if (banner.id === 25 && !isLm) {
          continue;
        }

        if (banner.id === 10 && isPh) {
          if (user.isAuth) {
            const rebateBanner = BANNERS_BY_ID.get(31);
            if (rebateBanner) result.push(rebateBanner);
          }
          continue;
        }

        if (banner.id === 12) {
          if (
            !(isPw && (locale === 'ru' || locale === 'uk')) &&
            !(isLm && locale === 'ru')
          ) {
            continue;
          }
        }

        const needsModification = banner.id === 9;

        const processedBanner = needsModification ? { ...banner } : banner;

        switch (processedBanner.id) {
          case 9:
            processedBanner.image =
              user.isAuth && loyaltyList?.currentLevel
                ? `${CROWN_IMAGE_URL}${+loyaltyList.currentLevel - 1}${isPh && mobileLandscape ? '_ph' : ''}.png`
                : processedBanner.image;
            break;
        }

        result.push(processedBanner);
      }

      return result;
    },
    [
      user.isAuth,
      loyaltyList,
      isLm,
      isPw,
      isPh,
      locale,
      mobileLandscape,
      bettingEnabled,
    ]
  );

  const sortedBanners = useMemo(() => {
    if (!user) return [];

    const deviceType = mobileLandscape ? 'mobile' : 'desktop';
    const authKey = user.isAuth ? 'authenticated' : 'unauthenticated';

    let pageKey: 'main' | 'games' | 'betting' = 'main';
    if (pathname.includes('/games')) pageKey = 'games';
    if (pathname.includes('/betting')) pageKey = 'betting';

    const rawOrder =
      isPh &&
      deviceType === 'mobile' &&
      (pageKey === 'main' || pageKey === 'games')
        ? SORT_CONFIG_BANNERS_PH_MOBILE[authKey][pageKey]
        : SORT_CONFIG_BANNERS[authKey][deviceType][pageKey] || [];

    return processBanners(rawOrder);
  }, [isPh, mobileLandscape, pathname, processBanners, user]);

  const sortedMobileBanners = useMemo(
    () => processBanners(SORT_CONFIG_BANNERS.mobileMenuModal),
    [processBanners]
  );

  return {
    categoryParam,
    sortedBanners,
    sortedMobileBanners,
    linkBanners,
    linkMobileBanners,
  };
};

export { useBanners };
